import React from 'react';
import { FlatList, Text, Pressable, View } from 'react-native';
import PropTypes from 'prop-types';
import { t } from 'Locale';
import { Device } from 'DeviceUtils';
import { logEvent } from 'AppManager';
import NavigationService from 'NavigationService';
import { FlashList } from '@shopify/flash-list';
import { getCarouselPackshotDimensionsByModelType } from 'Utils/index';
import ItemContainer from '../ItemContainer';
import Icon from '../Icon';
import defaultStyles from './Carousel.jss';

const RENDER_INITIAL_CAROUSEL_ITEMS_COUNT = Device.isTabletOrWeb ? 10 : 4;
const MIN_ITEMS_TO_SHOW_VIEW_ALL_BUTTON = 10;

class Carousel extends React.Component {
    constructor(props) {
        super(props);
        this.flatListRef = null;
        this.state = {
            currenPosition: 'home'
        };
    }

    componentDidUpdate(prevProps) {
        const { scrollIndex, items } = this.props;
        const { currenPosition } = this;
        this.state.currenPosition = NavigationService.getActiveMenuItem();
        if (
            scrollIndex !== prevProps.scrollIndex &&
            scrollIndex >= 0 &&
            scrollIndex < items.length
        ) {
            this.flatListRef.scrollToIndex({
                index: scrollIndex,
                animated: true,
                viewPosition: 0.5
            });
        }
    }

    onScroll = () => {
        const { title, isRecommended } = this.props;
        const subtype = isRecommended ? 'recommendation' : 'scroll';
        logEvent({ type: 'navigation', subtype, content1: title });
    };

    parseItems = (items) =>
        items.map((item, index) => {
            const { onItemPress, title, isRecommended } = this.props;
            item.listId =
                item.data?.id && item.data?.category
                    ? `${item.data.id}-${item.data.category}`
                    : `${index}`;
            if (item?.data?.completedInPercentage)
                item.listId += `-${item.data.completedInPercentage}`;

            const onPress = (carouselItem) => {
                const subtype = isRecommended ? 'recommendation' : 'select';
                logEvent({
                    type: 'navigation',
                    subtype,
                    content1: title,
                    content2: carouselItem?.getId()
                });
                onItemPress(carouselItem);
            };

            return { data: item, index, onItemPress: onPress };
        });

    renderItem = ({ item, index }) => {
        const { hideImages, forcePortraitViewPackshotDimensions, forceHideGradient, showMetadata } =
            this.props;
        const key = item.data?.data
            ? `${item.data.modelType}_${item.data.data.id}`
            : `unknown_asset_${index}`;

        return (
            <ItemContainer
                key={index}
                item={item}
                hideImage={hideImages}
                style={
                    index === 0
                        ? [defaultStyles.packshot, defaultStyles.firstPackshot]
                        : defaultStyles.packshot
                }
                forcePortraitViewDimensions={forcePortraitViewPackshotDimensions}
                forceHideGradient={forceHideGradient}
                showMetadata={showMetadata}
            />
        );
    };

    render() {
        const {
            items,
            title,
            onViewAllPress,
            style,
            showCount,
            overwriteCount,
            showMetadata,
            playlistUrl,
            displayViewAll,
            testID = title.replace(/ /g, '_'),
            keyboardShouldPersistTaps,
            forcePortraitViewPackshotDimensions
        } = this.props;
        const { currenPosition } = this.state;

        let estimatedItemSize =
            getCarouselPackshotDimensionsByModelType(
                items[0]?.modelType,
                forcePortraitViewPackshotDimensions
            )?.width || 129;

        if (Device.isWeb) {
            estimatedItemSize += defaultStyles.packshot.marginRight;
        }

        return (
            <View style={style} testID={`carousel_${testID}`}>
                <View style={defaultStyles.header}>
                    <Text style={defaultStyles.title}>
                        {title}
                        {showCount && (
                            <Text style={defaultStyles.itemCount}>{` (${
                                overwriteCount || items.length
                            })`}</Text>
                        )}
                    </Text>
                    {onViewAllPress &&
                        (displayViewAll || items?.length >= MIN_ITEMS_TO_SHOW_VIEW_ALL_BUTTON) && (
                            <Pressable
                                style={defaultStyles.button}
                                onPress={() => {
                                    onViewAllPress(title, showMetadata, playlistUrl, items);
                                    logEvent({
                                        type: 'user-flow',
                                        subtype: currenPosition,
                                        content1: 'carousel-show-more',
                                        content2: title,
                                        context: '/show-all'
                                    });
                                }}
                                testID={`more_${testID}`}
                            >
                                <Text style={defaultStyles.buttonText}>
                                    {t('COMMON.MORE_MOBILES')}
                                </Text>
                                <Icon style={defaultStyles.buttonIcon} name="arrow_right" />
                            </Pressable>
                        )}
                </View>
                <View>
                    <FlashList
                        ref={(ref) => {
                            this.flatListRef = ref;
                        }}
                        keyExtractor={(item) => `${title}-${item.data.listId}`}
                        // this prop produces an error
                        // but works and is necessary to make carousels look ok on FireFox
                        // …and merge overflow into containerCustomStyle instead
                        containerCustomStyle={[
                            defaultStyles.content,
                            Device.isWeb && {
                                overflow: 'hidden',
                                overflowX: 'hidden',
                                overflowY: 'hidden'
                            }
                        ]}
                        data={this.parseItems(items)}
                        onScroll={this.onScroll}
                        // scrollEventThrottle={5000}
                        renderItem={this.renderItem}
                        horizontal
                        onScrollToIndexFailed={() => {}}
                        keyboardShouldPersistTaps={keyboardShouldPersistTaps}
                        drawDistance={1}
                        estimatedItemSize={estimatedItemSize}
                        showsHorizontalScrollIndicator={false}
                    />
                </View>
            </View>
        );
    }
}

Carousel.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    style: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.number]),
    title: PropTypes.string,
    onViewAllPress: PropTypes.func,
    displayViewAll: PropTypes.bool,
    onItemPress: PropTypes.func,
    hideImages: PropTypes.bool,
    showCount: PropTypes.bool,
    overwriteCount: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    forcePortraitViewPackshotDimensions: PropTypes.bool,
    isRecommended: PropTypes.bool,
    scrollIndex: PropTypes.number,
    forceHideGradient: PropTypes.bool,
    showMetadata: PropTypes.bool,
    playlistUrl: PropTypes.string,
    testID: PropTypes.string
};

Carousel.defaultProps = {
    style: {},
    title: '',
    onViewAllPress: null,
    onItemPress: () => {},
    displayViewAll: false,
    // When carousels is not visible, we can hide images
    hideImages: false,
    showCount: false,
    overwriteCount: false,
    forcePortraitViewPackshotDimensions: Device.isPhone,
    isRecommended: false,
    scrollIndex: 0,
    forceHideGradient: false,
    showMetadata: false,
    playlistUrl: '',
    testID: null,
    keyboardShouldPersistTaps: 'never'
};

export default Carousel;
